import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

/**
 * Shared wrapper for legal / info pages.
 * Provides the Acta Documentum header, light blue background, footer links
 * and a clean reading column.
 */
export default function LegalLayout({ title, eyebrow, lastUpdate, children, testid }) {
  return (
    <div className="min-h-screen aurora-bg" data-testid={testid}>
      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[var(--do-bg-elev)] border-b border-[var(--do-border)]">
        <div className="max-w-5xl mx-auto h-16 px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid={`${testid}-logo-home`}>
            <div
              className="h-9 w-9 rounded-[10px] flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #2563EB, #1E3A8A)", boxShadow: "0 6px 18px -6px rgba(29, 78, 216, 0.55)" }}
            >
              <span className="text-[var(--do-on-accent)] font-display text-[13px] tracking-[0.18em]">A</span>
            </div>
            <span className="font-heading font-bold text-[15px] tracking-tight">Acta Documentum</span>
          </Link>
          <Link
            to="/"
            data-testid={`${testid}-back-home`}
            className="text-sm font-heading font-semibold text-[var(--do-ink)] hover:text-[var(--do-accent)] inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} weight="bold" /> Accueil
          </Link>
        </div>
      </header>

      {/* CONTENT */}
      <main className="relative max-w-3xl mx-auto px-6 py-14 lg:py-20">
        {eyebrow && <div className="label-eyebrow mb-3">{eyebrow}</div>}
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{title}</h1>
        {lastUpdate && (
          <div className="mt-3 text-xs text-[var(--do-muted)] font-mono-stat uppercase tracking-wider">
            Dernière mise à jour : {lastUpdate}
          </div>
        )}

        <article className="legal-prose mt-10">{children}</article>

        <div className="mt-16 pt-8 border-t border-[var(--do-border)] flex flex-wrap items-center justify-between gap-3 text-sm">
          <Link to="/" className="text-[var(--do-accent)] font-semibold hover:text-[var(--do-ink)] inline-flex items-center gap-1.5">
            <ArrowLeft size={13} weight="bold" /> Retour à l'accueil
          </Link>
          <Link
            to="/contact"
            className="au-btn-primary px-4 py-2 text-sm"
            data-testid={`${testid}-contact-cta`}
          >
            Nous contacter
          </Link>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-[var(--do-border)] bg-[var(--do-bg-elev)] backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-8 grid sm:grid-cols-2 gap-6 text-xs">
          <div className="text-[var(--do-muted)]">
            © 2026 Acta Documentum · Tous droits réservés
          </div>
          <div className="sm:text-right flex flex-wrap sm:justify-end gap-x-5 gap-y-2 text-[var(--do-muted)]">
            <Link to="/about" className="hover:text-[var(--do-accent)]">À propos</Link>
            <Link to="/contact" className="hover:text-[var(--do-accent)]">Contact</Link>
            <Link to="/legal/rgpd" className="hover:text-[var(--do-accent)]">RGPD</Link>
            <Link to="/legal/cgu" className="hover:text-[var(--do-accent)]">CGU</Link>
            <Link to="/legal/mentions" className="hover:text-[var(--do-accent)]">Mentions légales</Link>
            <Link to="/legal/confidentialite" className="hover:text-[var(--do-accent)]">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
